export const META_PIXEL_ID = '1050955704359293'

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

const SCRIPT_ID = 'meta-pixel-script'
const recentEvents = new Map<string, number>()
let initialized = false

/**
 * Injects the official Meta Pixel base code into <head> and initializes the pixel.
 * Loads asynchronously and is safe to call multiple times.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (initialized) return
  initialized = true

  if (!window.fbq) {
    const fbq: FbqFn = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    } as FbqFn

    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []

    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq
  }

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    const head = document.head || document.getElementsByTagName('head')[0]
    head.appendChild(script)

    const noscript = document.createElement('noscript')
    const img = document.createElement('img')
    img.height = 1
    img.width = 1
    img.style.display = 'none'
    img.alt = ''
    img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`
    noscript.appendChild(img)
    head.appendChild(noscript)
  }

  window.fbq?.('init', META_PIXEL_ID)
}

function shouldSkip(key: string, windowMs: number): boolean {
  const now = Date.now()
  const last = recentEvents.get(key)
  if (last !== undefined && now - last < windowMs) return true
  recentEvents.set(key, now)
  return false
}

/** Fires a standard Meta event, de-duplicated within a short window. */
export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
  dedupeMs = 1200,
): void {
  if (typeof window === 'undefined' || !window.fbq) return
  if (shouldSkip(`track:${name}`, dedupeMs)) return
  window.fbq('track', name, params ?? {})
}

/** Fires a custom Meta event, de-duplicated within a short window. */
export function trackCustomEvent(
  name: string,
  params?: Record<string, unknown>,
  dedupeMs = 1200,
): void {
  if (typeof window === 'undefined' || !window.fbq) return
  if (shouldSkip(`custom:${name}`, dedupeMs)) return
  window.fbq('trackCustom', name, params ?? {})
}

export type LinkIntent = 'contact' | 'checkout' | 'lead' | null

/** Classifies an outbound URL into the Meta event it should report. */
export function classifyLink(href: string): LinkIntent {
  const url = href.toLowerCase()
  if (!url) return null
  if (url.includes('wa.me') || url.includes('whatsapp')) return 'contact'
  if (url.includes('ringana')) return 'checkout'
  if (
    url.includes('comunidadfresh.com') ||
    url.includes('skin-fresh-index') ||
    url.includes('skinfreshindex') ||
    url.includes('quiz') ||
    url.includes('diagnostico')
  ) {
    return 'lead'
  }
  return null
}
